/**
 * Experience Data Transformer
 *
 * Single Responsibility: Transforms API responses to domain models
 * Open/Closed Principle: Can be extended without modifying existing code
 */

import {
  Experience,
  ExperienceListResponse,
} from '@/services/experienceService';

export interface ApiExperienceResponse {
  code?: number;
  success?: boolean;
  data?: {
    list?: Experience[];
    pagination?: {
      totalItems?: number;
      currentCount?: number;
      currentPage?: number;
      itemsPerPage?: number;
      hasNextPage?: boolean;
      hasPreviousPage?: boolean;
      totalPages?: number;
    };
  };
}

export interface ApiStatsResponse {
  code?: number;
  success?: boolean;
  data?: {
    signatureJourneys?: number;
    packages?: number;
    boutiqueStays?: number;
    guidedActivities?: number;
    ridersGuides?: number;
  };
}

/** Normalize attraction count from API (camelCase or snake_case) so mapper always sees attractionCount */
function normalizeAttractionCount(
  item: Experience & Record<string, unknown>,
  index: number,
  logSample: boolean
): number {
  const raw = {
    attractionCount: item.attractionCount,
    attraction_count: item.attraction_count,
    highlightsCount: item.highlightsCount,
    highlights_count: item.highlights_count,
    attractionsLength: item.attractions?.length,
  };
  const n =
    item.attractionCount ??
    item.attraction_count ??
    item.highlightsCount ??
    item.highlights_count;
  let value: number;
  if (typeof n === 'number' && !Number.isNaN(n)) {
    value = n;
  } else if (typeof n === 'string') {
    const parsed = parseInt(n, 10);
    value = Number.isNaN(parsed) ? (item.attractions?.length ?? 0) : parsed;
  } else {
    value = item.attractions?.length ?? 0;
  }
  if (logSample) {
    console.log('[ExperienceTransformer] normalizeAttractionCount sample', {
      index,
      title: item.title || item.name,
      raw,
      source:
        value === (item.attractions?.length ?? 0)
          ? 'attractions.length'
          : 'API field',
      normalized: value,
    });
  }
  return value;
}

export class ExperienceTransformer {
  /**
   * Transform API response to ExperienceListResponse.
   * Normalizes attractionCount on each item so list UI uses API count consistently (avoids stale mapper cache).
   */
  static transformExperienceList(
    apiResponse: ApiExperienceResponse
  ): ExperienceListResponse {
    const rawList = apiResponse?.data?.list || [];
    const pagination = apiResponse?.data?.pagination || {};
    console.log('[ExperienceTransformer] transformExperienceList', {
      listLength: rawList.length,
      firstItemKeys: rawList[0]
        ? Object.keys(rawList[0]).filter(k => /attraction|highlight/i.test(k))
        : [],
    });
    const experiences: Experience[] = rawList.map(
      (item: Experience & Record<string, unknown>, index: number) => ({
        ...item,
        attractionCount: normalizeAttractionCount(item, index, index < 2),
      })
    );

    return {
      experiences,
      total: pagination.totalItems || experiences.length,
      page: pagination.currentPage || 1,
      limit: pagination.itemsPerPage || experiences.length,
      hasMore: pagination.hasNextPage || false,
      totalPages: pagination.totalPages,
    };
  }

  /**
   * Extract experiences array from API response
   */
  static extractExperiences(apiResponse: ApiExperienceResponse): Experience[] {
    return apiResponse?.data?.list || [];
  }

  /**
   * Transform API stats response to ExperienceStats
   */
  static transformStats(apiResponse: ApiStatsResponse) {
    const apiData = apiResponse?.data || {};

    return {
      signatureJourneys: apiData.signatureJourneys ?? 0,
      curatedPackages: apiData.packages ?? 0,
      boutiqueStays: apiData.boutiqueStays ?? 0,
      guidedActivities: apiData.guidedActivities ?? 0,
      ridersGuides: apiData.ridersGuides ?? 0,
    };
  }

  /**
   * Build authentication headers
   */
  static buildAuthHeaders(
    accessToken?: string,
    refreshToken?: string
  ): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (accessToken && refreshToken) {
      headers['x-access-token'] = accessToken;
      headers['x-refresh-token'] = refreshToken;
    }

    return headers;
  }
}
